import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, Location } from 'react-router-dom';
import { useNavigationBlocker } from './useNavigationBlocker';
import { getWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import handleNavigation from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/HandleNavigation';

/**
 * Global navigation guard for booking wizard alterations.
 * Must be used at a high level in the component tree (e.g., Frontend component)
 * so it doesn't unmount when navigating away from the wizard.
 */
export function useBookingWizardNavigationGuard() {
	const location = useLocation();
	const navigate = useNavigate();

	const shouldBlock = useCallback(
		({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
			// Only block if currently in the booking wizard
			if (!currentLocation.pathname.includes('/booking-wizard/')) {
				return false;
			}

			// Check if we're in alteration mode
			const wizardData = getWizardData();
			const isAlteration = wizardData?.wizardMode === 'ALTERATION';

			// Don't block navigation within the booking wizard
			if (nextLocation.pathname.includes('/booking-wizard')) {
				return false;
			}

			// Don't block navigation from payment to success
			if (currentLocation.pathname.includes('payment') && nextLocation.pathname.includes('booking-success')) {
				return false;
			}

			if (!isAlteration) {
				return currentLocation.pathname.includes('payment');
			}

			// Block all navigation outside the booking wizard during alteration
			return true;
		},
		[],
	);

	const blocker = useNavigationBlocker(shouldBlock);

	// Handle blocked navigation
	useEffect(() => {
		if (blocker.state === 'blocked' && blocker.location) {
			const wizardData = getWizardData();
			const bookingId = wizardData?.bookingToEdit ?? '';

			handleNavigation(bookingId, navigate)
				.then(result => {
					if (result) {
						blocker.proceed();
					} else {
						blocker.reset();
					}
				})
				.catch(() => {
					blocker.reset();
				});
		}
	}, [blocker.state, blocker.location, navigate, blocker]);
}
